import {
  canonicalManagedConnectorId,
  MANAGED_AI_CONNECTOR_ID,
} from "./aiProviders/managedAiConnector";
import {
  canonicalPricingModelId,
  DEFAULT_MODEL_PRICING,
  MANAGED_MODEL_PRICING_ESTIMATES,
} from "./aiModelPricing";

export type UsagePriceRow = {
  spaceId: string | null;
  connectorId: string | null;
  measure: string;
  dimension: string | null;
  amountPerUnit: number;
};

export const SPEND_USD_MEASURE = "spend_usd";
export const SPEND_USD_SCALE = 1_000_000;

export const TOKEN_MEASURE_FIELDS = {
  input_tokens: "inputTokens",
  output_tokens: "outputTokens",
  cache_read_tokens: "cacheReadTokens",
  cache_write_tokens: "cacheWriteTokens",
} as const;

export const TOKEN_USAGE_MEASURES = Object.keys(
  TOKEN_MEASURE_FIELDS
) as (keyof typeof TOKEN_MEASURE_FIELDS)[];
export const TOKEN_USAGE_MEASURE_SET: ReadonlySet<string> = new Set(
  TOKEN_USAGE_MEASURES
);

export type TokenUsageTotals = {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
};

export function connectorSpendGroupKey(row: {
  day: number;
  connectorId: string;
  source: string;
  spaceId: string | null;
  dimension: string;
}) {
  return JSON.stringify([
    row.day,
    row.connectorId,
    row.source,
    row.spaceId ?? "",
    row.dimension,
  ]);
}

export function aggregateTokenUsage<
  Row extends {
    connectorId: string;
    measure: string;
    dimension: string;
    value: number;
  },
>(
  rows: readonly Row[],
  groupKey: (row: Row) => string
): Map<string, { first: Row; tokens: TokenUsageTotals }> {
  const groups = new Map<string, { first: Row; tokens: TokenUsageTotals }>();
  for (const row of rows) {
    const field =
      TOKEN_MEASURE_FIELDS[row.measure as keyof typeof TOKEN_MEASURE_FIELDS];
    if (!field || !row.dimension) continue;
    const key = groupKey(row);
    let group = groups.get(key);
    if (!group) {
      group = {
        first: row,
        tokens: {
          inputTokens: 0,
          outputTokens: 0,
          cacheReadTokens: 0,
          cacheWriteTokens: 0,
        },
      };
      groups.set(key, group);
    }
    group.tokens[field] += row.value;
  }
  return groups;
}

const MEASURE_PRICING_FIELD = {
  input_tokens: "inputPerMillion",
  output_tokens: "outputPerMillion",
  cache_read_tokens: "cacheReadPerMillion",
  cache_write_tokens: "cacheWritePerMillion",
} as const;

// List price per single metered token for a known model, used as a fallback
// when no rate has been entered.
export function defaultPricePerUnit(
  measure: string,
  model: string
): number | null {
  const field =
    MEASURE_PRICING_FIELD[measure as keyof typeof MEASURE_PRICING_FIELD];
  if (!field) return null;
  const pricing =
    DEFAULT_MODEL_PRICING[model] ??
    DEFAULT_MODEL_PRICING[canonicalPricingModelId(model)];
  const perMillion = pricing?.[field];
  return perMillion == null ? null : perMillion / 1_000_000;
}

export function priceKey(
  connectorId: string,
  measure: string,
  dimension: string
) {
  return `${connectorId}\0${measure}\0${dimension}`;
}

// The managed provider has no connector_usage_prices rows, so its sentinel
// spend is priced from the published list-price estimates (per-1M → per-unit).
export function managedAiDefaultPrices(): Map<string, number> {
  const map = new Map<string, number>();
  for (const [model, pricing] of Object.entries(
    MANAGED_MODEL_PRICING_ESTIMATES
  )) {
    const set = (measure: string, perMillion: number | undefined) => {
      if (perMillion == null) return;
      map.set(
        priceKey(MANAGED_AI_CONNECTOR_ID, measure, model),
        perMillion / 1_000_000
      );
    };
    set("input_tokens", pricing.inputPerMillion);
    set("output_tokens", pricing.outputPerMillion);
    set("cache_read_tokens", pricing.cacheReadPerMillion);
    set("cache_write_tokens", pricing.cacheWritePerMillion);
  }
  return map;
}

// Rows pinned to a space or AI provider are ignored — connector spend prices
// the connector-wide total.
export function buildUsagePriceLookup(
  rows: readonly UsagePriceRow[],
  defaults?: ReadonlyMap<string, number>
): Map<string, number> {
  const map = new Map<string, number>(defaults);
  for (const row of rows) {
    if (row.connectorId == null) continue;
    if (row.spaceId != null) continue;
    map.set(
      priceKey(row.connectorId, row.measure, row.dimension ?? ""),
      row.amountPerUnit
    );
  }
  return map;
}

export function priceFor(
  prices: ReadonlyMap<string, number>,
  connectorId: string,
  measure: string,
  dimension: string
): number | null {
  return (
    prices.get(priceKey(connectorId, measure, dimension)) ??
    defaultPricePerUnit(measure, dimension) ??
    null
  );
}

// input_tokens is the grand total that already includes cache reads and writes,
// so only the fresh remainder is charged the input rate or cached tokens bill
// twice.
export function estimateTokenCostUsd(
  connectorId: string,
  model: string,
  tokens: TokenUsageTotals,
  prices: ReadonlyMap<string, number>
): number | null {
  const id = canonicalManagedConnectorId(connectorId);
  const inputPrice = priceFor(prices, id, "input_tokens", model);
  const outputPrice = priceFor(prices, id, "output_tokens", model);
  const cacheReadPrice = priceFor(prices, id, "cache_read_tokens", model);
  const cacheWritePrice = priceFor(prices, id, "cache_write_tokens", model);
  if (
    inputPrice == null &&
    outputPrice == null &&
    cacheReadPrice == null &&
    cacheWritePrice == null
  ) {
    return null;
  }
  const freshInput = Math.max(
    0,
    tokens.inputTokens - tokens.cacheReadTokens - tokens.cacheWriteTokens
  );
  return (
    freshInput * (inputPrice ?? 0) +
    tokens.outputTokens * (outputPrice ?? 0) +
    tokens.cacheReadTokens * (cacheReadPrice ?? 0) +
    tokens.cacheWriteTokens * (cacheWritePrice ?? 0)
  );
}

export function missingTokenPrices(
  connectorId: string,
  model: string,
  tokens: TokenUsageTotals,
  prices: ReadonlyMap<string, number>
): string[] {
  const id = canonicalManagedConnectorId(connectorId);
  const missing: string[] = [];
  const freshInput = Math.max(
    0,
    tokens.inputTokens - tokens.cacheReadTokens - tokens.cacheWriteTokens
  );
  for (const [measure, value] of [
    ["input_tokens", freshInput],
    ["output_tokens", tokens.outputTokens],
    ["cache_read_tokens", tokens.cacheReadTokens],
    ["cache_write_tokens", tokens.cacheWriteTokens],
  ] as const) {
    if (value > 0 && priceFor(prices, id, measure, model) == null) {
      missing.push(measure);
    }
  }
  return missing;
}

export function estimateTokenCostMicroUsd(
  connectorId: string,
  model: string,
  tokens: TokenUsageTotals,
  prices: ReadonlyMap<string, number>
): number | null {
  const costUsd = estimateTokenCostUsd(connectorId, model, tokens, prices);
  return costUsd == null ? null : Math.round(costUsd * SPEND_USD_SCALE);
}
